const SOURCE_LABELS = {
  app_manual: "User manual (app)",
  irrigation_schedule: "Scheduled irrigation",
  irrigation_auto_approved: "Smart irrigation (approved)",
  irrigation_auto_no_response: "Smart irrigation (auto ON)",
  automation_rule_render: "Smart rule",
};

const MIN_VALID_MS = Date.UTC(2020, 0, 1);

/** Normalize unix seconds or ms to ms. Reject garbage / 1970-era values. */
export function normalizeTimestampMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  let ms = n;
  if (n < 1e12) {
    if (n < 1e9) return 0;
    ms = n * 1000;
  }
  if (ms < MIN_VALID_MS) return 0;
  return ms;
}

export function triggerKind(source) {
  switch (String(source || "")) {
    case "app_manual":
      return "user";
    case "irrigation_schedule":
      return "schedule";
    case "irrigation_auto_approved":
    case "irrigation_auto_no_response":
      return "smart_irrigation";
    case "automation_rule_render":
      return "smart_rule";
    default:
      return "other";
  }
}

export function sourceLabel(source, ruleContext = {}) {
  const ctx = ruleContext || {};
  if (source === "irrigation_schedule" && ctx.label) return `Schedule: ${ctx.label}`;
  if (source === "automation_rule_render" && ctx.label) return `Smart rule: ${ctx.label}`;
  return SOURCE_LABELS[source] || (source ? String(source).replace(/_/g, " ") : "Unknown");
}

export function formatCountdown(totalSec) {
  const s = Math.max(0, Math.round(Number(totalSec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export function isPlaceholderSensorSnapshot(sensors = {}) {
  const values = [sensors.tempAtStart, sensors.humidityAtStart, sensors.soilAtStart].filter(
    (v) => v != null && Number.isFinite(Number(v))
  );
  if (!values.length) return true;
  return values.every((v) => Number(v) === 0);
}

export function sanitizeSensorSnapshot(sensors = {}) {
  const snap = {
    tempAtStart: Number.isFinite(Number(sensors.tempAtStart)) ? Number(sensors.tempAtStart) : null,
    humidityAtStart: Number.isFinite(Number(sensors.humidityAtStart))
      ? Number(sensors.humidityAtStart)
      : null,
    soilAtStart: Number.isFinite(Number(sensors.soilAtStart)) ? Number(sensors.soilAtStart) : null,
  };
  if (isPlaceholderSensorSnapshot(snap)) {
    return { tempAtStart: null, humidityAtStart: null, soilAtStart: null };
  }
  return snap;
}

export function formatSensorValue(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n;
}

export function sensorsFromCommand(cmd = {}) {
  const snap = cmd.sensorAtStart || cmd.ruleContext?.sensorAtStart || {};
  const temp = snap.temperature ?? snap.Temperature ?? cmd.tempAtStart;
  const humidity = snap.humidity ?? snap.Humidity ?? cmd.humidityAtStart;
  const soil = snap.soilMoisture ?? snap.SoilMoisture ?? cmd.soilAtStart;
  return sanitizeSensorSnapshot({
    tempAtStart: Number.isFinite(Number(temp)) ? Number(temp) : null,
    humidityAtStart: Number.isFinite(Number(humidity)) ? Number(humidity) : null,
    soilAtStart: Number.isFinite(Number(soil)) ? Number(soil) : null,
  });
}

export function sensorsFromLive(live = {}) {
  return sanitizeSensorSnapshot({
    tempAtStart: live.Temperature ?? live.temperature ?? live.t ?? null,
    humidityAtStart: live.Humidity ?? live.humidity ?? live.h ?? null,
    soilAtStart: live.SoilMoisture ?? live.soilMoisture ?? live.s ?? null,
  });
}

function pumpRunHasSensors(run = {}) {
  const snap = sanitizeSensorSnapshot(run);
  return (
    snap.soilAtStart != null || snap.tempAtStart != null || snap.humidityAtStart != null
  );
}

function mergeSensorFields(run = {}, sensors = null) {
  const base = sanitizeSensorSnapshot(run);
  const incoming = sanitizeSensorSnapshot(sensors || {});
  return {
    ...run,
    tempAtStart: base.tempAtStart ?? incoming.tempAtStart,
    humidityAtStart: base.humidityAtStart ?? incoming.humidityAtStart,
    soilAtStart: base.soilAtStart ?? incoming.soilAtStart,
  };
}

export function sensorsFromHistoryMap(sensorMap = {}, atMs) {
  const target = normalizeTimestampMs(atMs);
  if (!target || !sensorMap || typeof sensorMap !== "object") return null;

  let best = null;
  let bestDiff = Infinity;
  for (const data of Object.values(sensorMap)) {
    const savedAt = normalizeTimestampMs(data?.savedAt);
    if (!savedAt) continue;
    const candidate = sanitizeSensorSnapshot({
      tempAtStart: data.Temperature,
      humidityAtStart: data.Humidity,
      soilAtStart: data.SoilMoisture,
    });
    if (!pumpRunHasSensors(candidate)) continue;
    const diff = Math.abs(savedAt - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = candidate;
    }
  }
  if (!best || bestDiff > 3 * 60 * 60 * 1000) return null;
  return best;
}

/** Fill missing At-start sensors from command payload or nearest hourly snapshot. */
export function enrichPumpRunSensors(
  run = {},
  { commands = [], sensorMap = {}, liveSnapshot = null } = {}
) {
  let next = sanitizeSensorSnapshot(run);
  next = { ...run, ...next };
  if (pumpRunHasSensors(next)) return next;

  next = mergeSensorFields(next, sensorsFromCommand(run));
  if (pumpRunHasSensors(next)) return next;

  const onId = run.onCommandId || run.id;
  const byId = commands.find((c) => String(c.commandId) === String(onId));
  if (byId) {
    next = mergeSensorFields(next, sensorsFromCommand(byId));
    if (pumpRunHasSensors(next)) return next;
  }

  const queuedAt = normalizeTimestampMs(run.queuedAt);
  if (queuedAt) {
    const byTime = commands.find(
      (c) =>
        String(c.action || "").toLowerCase() === "on" &&
        Math.abs(normalizeTimestampMs(c.issuedAt) - queuedAt) <= 5000
    );
    if (byTime) {
      next = mergeSensorFields(next, sensorsFromCommand(byTime));
      if (pumpRunHasSensors(next)) return next;
    }
  }

  next = mergeSensorFields(
    next,
    sensorsFromHistoryMap(sensorMap, run.startedAt || run.queuedAt)
  );
  if (pumpRunHasSensors(next)) return next;

  if (liveSnapshot && (run.status === "running" || run.status === "queued")) {
    next = mergeSensorFields(next, sensorsFromLive(liveSnapshot));
  }

  return next;
}

function durationBetween(start, end) {
  const s = normalizeTimestampMs(start);
  const e = normalizeTimestampMs(end);
  if (!s || !e || e < s) return null;
  return Math.max(0, Math.round((e - s) / 1000));
}

function parseRanSecFromMessage(message = "") {
  const match = String(message).match(/ranSec\s*=\s*(\d+)/i);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

function pairOnOffCommands(items = []) {
  const sorted = [...items].sort(
    (a, b) => normalizeTimestampMs(a.issuedAt) - normalizeTimestampMs(b.issuedAt)
  );
  const pairs = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const cmd = sorted[i];
    if (String(cmd.action).toLowerCase() !== "on") continue;
    let offCmd = null;
    for (let j = i + 1; j < sorted.length; j += 1) {
      const next = sorted[j];
      const nextAction = String(next.action).toLowerCase();
      if (nextAction === "off") {
        offCmd = next;
        break;
      }
      if (nextAction === "on") break;
    }
    pairs.push({ onCmd: cmd, offCmd });
  }
  return pairs;
}

function buildRunFromPair(onCmd, offCmd) {
  const onAt = normalizeTimestampMs(onCmd.issuedAt);
  const startedAt =
    normalizeTimestampMs(onCmd.ackAt) ||
    normalizeTimestampMs(onCmd.sentAt) ||
    onAt;
  let endedAt = offCmd
    ? normalizeTimestampMs(offCmd.ackAt) || normalizeTimestampMs(offCmd.issuedAt)
    : 0;
  const plannedDurationSec = Number(onCmd.durationSec) || 120;
  const durationMs = plannedDurationSec * 1000;
  const ranFromOff = offCmd ? parseRanSecFromMessage(offCmd.ackMessage) : null;

  let status = "queued";
  let stopReason = offCmd?.ackMessage || onCmd.failureReason || "";
  if (onCmd.status === "failed") status = "failed";
  else if (offCmd && offCmd.status === "acked") status = "stopped";
  else if (onCmd.status === "acked" || onCmd.status === "sent") {
    if (endedAt) status = "stopped";
    else if (startedAt && Date.now() - startedAt > durationMs) {
      status = "completed";
      endedAt = startedAt + durationMs;
      stopReason = stopReason || "auto_off";
    } else status = "running";
  } else if (onCmd.status === "pending") status = "queued";

  const src = onCmd.source || "app_manual";
  const actualDurationSec =
    ranFromOff !== null
      ? ranFromOff
      : endedAt && startedAt
        ? durationBetween(startedAt, endedAt)
        : status === "completed"
          ? plannedDurationSec
          : null;

  const sensors = sensorsFromCommand(onCmd);

  return {
    id: String(onCmd.commandId || onCmd._id || onAt),
    onCommandId: String(onCmd.commandId || onCmd._id || ""),
    source: src,
    sourceLabel: sourceLabel(src, onCmd.ruleContext),
    triggerKind: triggerKind(src),
    queuedAt: onAt,
    startedAt,
    endedAt,
    plannedDurationSec,
    actualDurationSec,
    status,
    stopReason,
    warning: onCmd.status === "failed" ? onCmd.failureReason || onCmd.ackMessage || "" : "",
    soilAtStart: sensors.soilAtStart,
    tempAtStart: sensors.tempAtStart,
    humidityAtStart: sensors.humidityAtStart,
    synthetic: true,
  };
}

export function synthesizePumpRunsFromCommands(items = []) {
  return pairOnOffCommands(items)
    .map(({ onCmd, offCmd }) => buildRunFromPair(onCmd, offCmd))
    .filter((r) => r.queuedAt > 0)
    .sort((a, b) => (Number(b.queuedAt) || 0) - (Number(a.queuedAt) || 0));
}

export function normalizePumpRun(run = {}) {
  const queuedAt = normalizeTimestampMs(run.queuedAt);
  let startedAt = normalizeTimestampMs(run.startedAt);
  if (!startedAt && run.status !== "queued") startedAt = queuedAt;
  let endedAt = normalizeTimestampMs(run.endedAt);
  const plannedDurationSec = Number(run.plannedDurationSec) || 0;
  const plannedMs = plannedDurationSec * 1000;
  let status = run.status || "queued";
  let stopReason = run.stopReason || "";
  let actualDurationSec =
    run.actualDurationSec !== null && run.actualDurationSec !== undefined
      ? Number(run.actualDurationSec)
      : null;

  if (status === "queued" && !normalizeTimestampMs(run.startedAt)) {
    return {
      ...run,
      queuedAt,
      startedAt: queuedAt || 0,
      endedAt: 0,
      status,
      stopReason,
      plannedDurationSec,
      actualDurationSec: null,
    };
  }

  const timerElapsed = startedAt && plannedMs && Date.now() >= startedAt + plannedMs;
  if (!endedAt && startedAt && plannedMs && (status === "completed" || timerElapsed)) {
    endedAt = startedAt + plannedMs;
    status = "completed";
    stopReason = stopReason || "auto_off";
    if (actualDurationSec === null) actualDurationSec = plannedDurationSec;
  }

  if (endedAt && startedAt && actualDurationSec === null) {
    actualDurationSec = durationBetween(startedAt, endedAt);
  }

  if (actualDurationSec !== null && plannedDurationSec > 0) {
    actualDurationSec = Math.min(actualDurationSec, plannedDurationSec + 120);
  }

  return {
    ...run,
    queuedAt,
    startedAt,
    endedAt,
    status,
    stopReason,
    plannedDurationSec,
    actualDurationSec,
  };
}

function findInFlightCommand(commands = [], nowMs = Date.now()) {
  return (
    commands.find((c) => {
      const status = String(c.status || "").toLowerCase();
      if (status !== "pending" && status !== "sent") return false;
      const issuedAt = normalizeTimestampMs(c.issuedAt);
      const durationSec = Number(c.durationSec) || 120;
      // Hub crashed / never acked: don't lock UI forever after the planned window
      if (status === "sent" && issuedAt && nowMs - issuedAt > (durationSec + 25) * 1000) {
        return false;
      }
      return true;
    }) || null
  );
}

function findActiveOnRun(commands = [], nowMs = Date.now()) {
  const sorted = [...commands].sort(
    (a, b) => normalizeTimestampMs(b.issuedAt) - normalizeTimestampMs(a.issuedAt)
  );
  for (const onCmd of sorted) {
    if (String(onCmd.action).toLowerCase() !== "on" || onCmd.status !== "acked") continue;
    const msg = String(onCmd.ackMessage || "").toLowerCase();
    // Hub may ack failure codes as failed status; never treat those as running.
    if (msg === "node_not_paired" || msg === "pump_busy" || msg.startsWith("fail")) continue;
    const onAt = normalizeTimestampMs(onCmd.issuedAt);
    // Countdown starts when the hub confirms the valve opened, not when the app tapped Water.
    const startedAt = normalizeTimestampMs(onCmd.ackAt);
    if (!startedAt) continue;
    const durationMs = (Number(onCmd.durationSec) || 120) * 1000;
    const offAfter = sorted.find(
      (c) =>
        String(c.action).toLowerCase() === "off" &&
        normalizeTimestampMs(c.issuedAt) >= onAt &&
        String(c.status).toLowerCase() === "acked"
    );
    if (!offAfter && startedAt && nowMs - startedAt < durationMs) {
      return {
        onCmd,
        startedAt,
        durationMs,
        remainingSec: Math.ceil((durationMs - (nowMs - startedAt)) / 1000),
      };
    }
  }
  return null;
}

/** Friendly pot label for a pump command (e.g. "Pot 1 (node_001)"). */
export function potLabelFromCommand(cmd = {}, nodes = []) {
  const nodeId = String(cmd?.nodeId || cmd?.ruleContext?.nodeId || "").trim();
  const potIndex = Number(cmd?.potIndex || cmd?.ruleContext?.potIndex || 0) || 0;
  const list = Array.isArray(nodes) ? nodes : [];
  const match =
    (nodeId && list.find((n) => String(n.nodeId) === nodeId)) ||
    (potIndex ? list.find((n) => Number(n.potIndex) === potIndex) : null) ||
    null;
  if (match) {
    const name = String(match.name || match.nodeId || "").trim();
    const pot = Number(match.potIndex || 0) ? `Pot ${match.potIndex}` : "";
    if (pot && name && name !== pot && !/^pot\s*\d+$/i.test(name)) return `${pot} · ${name}`;
    return pot || name || nodeId || "this pot";
  }
  if (potIndex) return nodeId ? `Pot ${potIndex} · ${nodeId}` : `Pot ${potIndex}`;
  return nodeId || "this pot";
}

const QUIET_PUMP_FAILURES = new Set([
  "auto_cleared_stale",
  "ack_timeout",
  "ack_retry",
  "ack_failed",
]);

export function isQuietPumpFailure(raw = "") {
  return QUIET_PUMP_FAILURES.has(String(raw || "").trim().toLowerCase());
}

/** User-facing text for hub/device ackMessage / failureReason codes. */
export function humanizePumpAckMessage(raw = "") {
  const key = String(raw || "").trim().toLowerCase();
  if (isQuietPumpFailure(key)) return "";
  const map = {
    node_not_paired: "That pot is not connected. Power the node near the hub, then try Water again.",
    valve_ack_timeout: "The valve did not open. Check the node power and the relay, then try again.",
    pump_busy: "The pump is already running. Try again in a moment.",
    unsupported_target: "That command could not be run.",
    unsupported_action: "That command could not be run.",
    irrigate_started: "Watering started.",
    pump_off: "Pump turned off.",
  };
  if (map[key]) return map[key];
  if (!raw) return "";
  if (/command poll|reflash|serial|http\s*-?\d+/i.test(String(raw))) return "";
  return "";
}

/**
 * Pump UI phases: idle | sending | sent | running | stopping | failed
 */
export function derivePumpUiState(commands = [], { localBusy = false, nowMs = Date.now() } = {}) {
  if (localBusy) {
    return {
      phase: "sending",
      locked: true,
      canOn: false,
      canOff: false,
      running: false,
      badge: "Sending",
      summary: "Sending command…",
      latest: null,
      activeCommand: null,
    };
  }

  const inFlight = findInFlightCommand(commands, nowMs);
  if (inFlight) {
    const action = String(inFlight.action).toLowerCase();
    if (action === "off") {
      return {
        phase: "stopping",
        locked: true,
        canOn: false,
        canOff: false,
        running: false,
        badge: "Stopping",
        summary: "OFF · sent to device",
        latest: inFlight,
        activeCommand: null,
        remainingSec: null,
      };
    }
    return {
      phase: "sent",
      locked: true,
      canOn: false,
      canOff: true,
      running: false,
      badge: "Waiting",
      summary: "Waiting for the valve — timer starts when the relay clicks",
      latest: inFlight,
      activeCommand: inFlight,
      remainingSec: null,
    };
  }

  const active = findActiveOnRun(commands, nowMs);
  if (active) {
    return {
      phase: "running",
      locked: true,
      canOn: false,
      canOff: true,
      running: true,
      badge: "Running",
      summary: `ON · ${formatCountdown(active.remainingSec)} left`,
      latest: active.onCmd,
      activeCommand: active.onCmd,
      remainingSec: active.remainingSec,
    };
  }

  const latest = [...commands].sort(
    (a, b) => normalizeTimestampMs(b.issuedAt) - normalizeTimestampMs(a.issuedAt)
  )[0];

  if (latest?.status === "failed") {
    const raw = latest.ackMessage || latest.failureReason || "";
    const detail = humanizePumpAckMessage(raw);
    if (!isQuietPumpFailure(raw) && detail) {
      return {
        phase: "failed",
        locked: false,
        canOn: true,
        canOff: true,
        running: false,
        badge: "Failed",
        summary: detail,
        latest,
        activeCommand: null,
      };
    }
  }

  return {
    phase: "idle",
    locked: false,
    canOn: true,
    canOff: true,
    running: false,
    badge: "Off",
    summary: "Off · ready",
    latest: latest || null,
    activeCommand: null,
  };
}

export function relayLogsFromCommands(commands = []) {
  return commands
    .filter((c) => String(c.target || "pump").toLowerCase() === "pump")
    .map((c) => {
      const at = normalizeTimestampMs(c.issuedAt);
      if (!at) return null;
      const action = String(c.action || "").toLowerCase();
      const src = c.source || "app_manual";
      return {
        id: `cmd_${c.commandId || at}`,
        level: c.status === "failed" ? "warning" : "info",
        source: "pump",
        message:
          action === "on"
            ? `Motor ON — ${sourceLabel(src, c.ruleContext)} — planned ${Number(c.durationSec) || 120}s`
            : `Motor OFF — User manual (app)`,
        at,
      };
    })
    .filter(Boolean);
}

export function pumpRunRemainingSec(run = {}) {
  if (run.status !== "running") return null;
  const startedAt = normalizeTimestampMs(run.startedAt) || normalizeTimestampMs(run.queuedAt);
  const plannedSec = Number(run.plannedDurationSec) || 0;
  if (!startedAt || !plannedSec) return null;
  return Math.max(0, Math.ceil(plannedSec - (Date.now() - startedAt) / 1000));
}

export function pumpRunNote(run = {}) {
  const snap = sanitizeSensorSnapshot(run);
  const parts = [];
  if (run.stopReason) {
    const map = {
      auto_off: "Auto off (timer)",
      user_off: "User OFF (manual)",
      superseded_by_manual_off: "Manual OFF",
      superseded_by_new_on: "Replaced by new ON",
    };
    parts.push(map[run.stopReason] || String(run.stopReason).replace(/_/g, " "));
  } else if (run.status === "completed") {
    parts.push("Auto off (timer)");
  }
  if (run.actualDurationSec != null && Number.isFinite(Number(run.actualDurationSec))) {
    const s = Math.round(Number(run.actualDurationSec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    parts.push(m ? `Ran ${m}m ${r}s` : `Ran ${r}s`);
  }
  if (pumpRunHasSensors(snap)) {
    parts.push(
      `At start: ${formatSensorValue(snap.tempAtStart)}°C · ${formatSensorValue(snap.humidityAtStart)}% humidity · ${formatSensorValue(snap.soilAtStart)}% soil`
    );
  }
  if (run.warning) parts.push(run.warning);
  return parts.length ? parts.join(" · ") : "—";
}
